import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography, LinearProgress } from '@mui/material';
import type { WizardState } from '../agent/types';

interface Props {
  wizard: WizardState;
  onAnswer: (value: any) => void;
}

export default function AgentWizardInput({ wizard, onAnswer }: Props) {
  const field = wizard.fields[wizard.currentIndex];
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 質問が切り替わるたびに入力をクリア & フォーカス
  useEffect(() => {
    setText('');
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [wizard.currentIndex]);

  const progress = ((wizard.currentIndex + 1) / wizard.fields.length) * 100;

  const handleSubmit = () => {
    if (field.inputType === 'number') {
      const n = parseInt(text, 10);
      onAnswer(Number.isFinite(n) ? n : null);
      return;
    }
    onAnswer(text.trim() || null);
  };

  const handleSkip = () => onAnswer(null);

  return (
    <Box sx={{ p: 1, background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          質問 {wizard.currentIndex + 1} / {wizard.fields.length}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
        />
      </Box>

      {field.inputType === 'yes_no' ? (
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            onClick={() => onAnswer(true)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            はい
          </Button>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={() => onAnswer(false)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            いいえ
          </Button>
          {!field.required && (
            <Button
              variant="text"
              color="inherit"
              size="small"
              onClick={handleSkip}
              sx={{ textTransform: 'none', minWidth: 72 }}
            >
              {field.skipLabel || 'スキップ'}
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'stretch' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            type={field.inputType === 'number' ? 'number' : 'text'}
            placeholder={field.placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              const ne: any = e.nativeEvent;
              if (ne?.isComposing || ne?.keyCode === 229) return;
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            // iOS Safari は font-size 16px 未満で自動ズームするため 16px に統一
            inputProps={{ style: { fontSize: 16 } }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={field.required && !text.trim()}
            sx={{ textTransform: 'none' }}
          >
            送信
          </Button>
          {!field.required && (
            <Button
              variant="text"
              color="inherit"
              size="small"
              onClick={handleSkip}
              sx={{ textTransform: 'none', minWidth: 72 }}
            >
              {field.skipLabel || 'スキップ'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
